import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiGift,
  FiDownload,
  FiEye,
  FiSend,
  FiRefreshCw,
  FiBarChart2,
  FiCopy,
  FiSearch
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { referralAPI, adminAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const ReferralManagement = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [referralUsers, setReferralUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [rewardForm, setRewardForm] = useState({
    userId: '',
    type: 'registration_bonus',
    phoneNumber: ''
  });
  const { showSuccess, showError } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [qrGenerating, setQrGenerating] = useState(false);
  const [qrParams, setQrParams] = useState({ code: '', userId: '' });
  const [qrData, setQrData] = useState(null);
  const [qrSearch, setQrSearch] = useState('');
  const [qrUsers, setQrUsers] = useState([]);
  const [qrSaved, setQrSaved] = useState([]);

  useEffect(() => {
    const path = location.pathname || '';
    if (path.endsWith('/users')) setActiveTab('users');
    else if (path.endsWith('/qr')) setActiveTab('qr');
    else if (path.endsWith('/stats') || path.endsWith('/admin/referrals')) setActiveTab('overview');
  }, [location.pathname]);

  const fetchReferralStats = useCallback(async () => {
    try {
      const response = await referralAPI.getAdminStats();
      setStats(response.data);
    } catch (error) {
      showError('Failed to load referral statistics');
    }
  }, [showError]);

  const fetchReferralUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await referralAPI.getAdminUsers({
        page: currentPage,
        limit: 20,
        search: searchTerm
      });
      setReferralUsers(response.data?.users || []);
      setTotalPages(response.data?.pagination?.pages || 1);
    } catch (error) {
      showError('Failed to load referral users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, showError]);

  useEffect(() => {
    fetchReferralStats();
    fetchReferralUsers();
  }, [selectedTimeRange, currentPage, searchTerm, fetchReferralStats, fetchReferralUsers]);

  useEffect(() => {
    if (activeTab === 'qr') {
      const load = async () => {
        try {
          setLoading(true);
          const response = await adminAPI.getAllUsers({ page: 1, limit: 50, search: qrSearch });
          const users = response.data?.data || [];
          setQrUsers(users);
        } catch (_) {}
        finally {
          setLoading(false);
        }
      };
      load();
    }
  }, [activeTab, qrSearch]);

  useEffect(() => {
    if (activeTab === 'qr') {
      try {
        const saved = JSON.parse(localStorage.getItem('adminQRCodes') || '[]');
        setQrSaved(Array.isArray(saved) ? saved : []);
      } catch (_) {
        setQrSaved([]);
      }
    }
  }, [activeTab]);

  const fetchAdminQRCode = async () => {
    try {
      setQrGenerating(true);
      setQrData(null);
      const payload = {};
      if (qrParams.code) payload.code = qrParams.code.trim();
      if (qrParams.userId) payload.userId = qrParams.userId.trim();
      const response = await referralAPI.getAdminQRCode(payload);
      const data = response.data || response;
      setQrData(data);
      try {
        const entry = {
          referralCode: data.referralCode || payload.code || '',
          referralUrl: data.referralUrl || '',
          qrCode: data.qrCode || '',
          userId: payload.userId || '',
          assigned: !!data.assigned,
          createdAt: new Date().toISOString()
        };
        const saved = JSON.parse(localStorage.getItem('adminQRCodes') || '[]');
        const list = Array.isArray(saved) ? saved : [];
        const filtered = list.filter(
          (i) =>
            (entry.referralCode && i.referralCode !== entry.referralCode) ||
            (!entry.referralCode && entry.userId && i.userId !== entry.userId)
        );
        const updated = [entry, ...filtered].slice(0, 20);
        localStorage.setItem('adminQRCodes', JSON.stringify(updated));
        setQrSaved(updated);
      } catch (_) {}
    } catch (error) {
      showError('Failed to generate QR code');
    } finally {
      setQrGenerating(false);
    }
  };

  const handleSendReward = async (e) => {
    e.preventDefault();
    try {
      const response = await referralAPI.sendReward(rewardForm);
      if (response.success) {
        showSuccess('Reward sent successfully');
        setShowRewardModal(false);
        setRewardForm({ userId: '', type: 'registration_bonus', phoneNumber: '' });
        fetchReferralStats();
        fetchReferralUsers();
      } else {
        showError(response.message || 'Failed to send reward');
      }
    } catch (error) {
      showError('Failed to send reward');
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);
  const formatNumber = (num) => new Intl.NumberFormat().format(num || 0);

  const getStatusBadge = (status) => {
    const map = {
      completed: 'bg-emerald-100 text-emerald-800',
      sent: 'bg-emerald-100 text-emerald-800',
      pending: 'bg-amber-100 text-amber-800',
      failed: 'bg-red-100 text-red-800'
    };
    return map[status] || 'bg-slate-100 text-slate-700';
  };

  const copyToClipboard = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(label);
    } catch (e) {
      showError('Failed to copy');
    }
  };

  if (loading && !stats && activeTab !== 'qr') {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Referral Management</h1>
            <p className="text-sm text-slate-500 mt-0.5">Monitor and manage the referral program.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 3 months</option>
              <option value="1y">Last year</option>
            </select>
            <button
              type="button"
              onClick={() => setShowRewardModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
            >
              <FiSend className="h-4 w-4" />
              Send Reward
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <button
            type="button"
            onClick={() => { setActiveTab('overview'); navigate('/admin/referrals'); }}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'overview' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <FiBarChart2 className="h-4 w-4" />
            Overview
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('users'); navigate('/admin/referrals/users'); }}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'users' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <FiUsers className="h-4 w-4" />
            Referral Users
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('rewards')}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'rewards' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <FiGift className="h-4 w-4" />
            Reward History
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('qr'); navigate('/admin/referrals/qr'); }}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'qr' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <FiDownload className="h-4 w-4" />
            QR Generator
          </button>
        </div>

        {/* Overview / Stats */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <FiUsers className="h-4 w-4" />
                  <span className="text-sm font-medium">Total Referrers</span>
                </div>
                <div className="text-2xl font-semibold text-slate-900">{formatNumber(stats?.totalReferrers)}</div>
                <div className="text-xs text-slate-500 mt-0.5">+{stats?.newReferrersThisPeriod || 0} this period</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <FiTrendingUp className="h-4 w-4" />
                  <span className="text-sm font-medium">Total Referrals</span>
                </div>
                <div className="text-2xl font-semibold text-slate-900">{formatNumber(stats?.totalReferrals)}</div>
                <div className="text-xs text-slate-500 mt-0.5">+{stats?.newReferralsThisPeriod || 0} this period</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <FiDollarSign className="h-4 w-4" />
                  <span className="text-sm font-medium">Total Rewards Paid</span>
                </div>
                <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats?.totalRewardsPaid || 0)}</div>
                <div className="text-xs text-slate-500 mt-0.5">{stats?.rewardsThisPeriod || 0} rewards this period</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <FiGift className="h-4 w-4" />
                  <span className="text-sm font-medium">Success Rate</span>
                </div>
                <div className="text-2xl font-semibold text-slate-900">
                  {((stats?.successfulRewards / stats?.totalRewardAttempts) * 100 || 0).toFixed(1)}%
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{stats?.successfulRewards || 0} / {stats?.totalRewardAttempts || 0} successful</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                  <h3 className="text-sm font-medium text-slate-900">Top Referrers</h3>
                </div>
                <div className="p-4">
                  {stats?.topReferrers?.length ? (
                    <ul className="divide-y divide-slate-100">
                      {stats.topReferrers.map((referrer, index) => (
                        <li key={referrer.id} className="py-3 flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700 shrink-0">#{index + 1}</span>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-900">{referrer.name}</div>
                            <div className="text-xs text-slate-500">{referrer.totalReferrals} referrals · {formatCurrency(referrer.totalEarnings)}</div>
                          </div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 shrink-0">{referrer.conversionRate}%</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500 py-4">No top referrers yet.</p>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-slate-900">Recent Referral Activity</h3>
                  <button type="button" onClick={fetchReferralStats} className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
                    <FiRefreshCw className="h-3.5 w-3.5" /> Refresh
                  </button>
                </div>
                <div className="p-4">
                  {stats?.recentActivity?.length ? (
                    <ul className="divide-y divide-slate-100">
                      {stats.recentActivity.map((activity, index) => (
                        <li key={index} className="py-3 flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                            <FiUsers className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-900">{activity.description}</div>
                            <div className="text-xs text-slate-500">{activity.timeAgo}</div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${getStatusBadge(activity.status)}`}>{activity.status}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500 py-4">No recent activity.</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Referral Users */}
        {activeTab === 'users' && (
          <>
            <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative flex-1 max-w-sm">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  placeholder="Search referral users..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
                />
              </div>
              <button type="button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
                <FiDownload className="h-4 w-4" />
                Export
              </button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              {loading ? (
                <div className="flex justify-center py-16"><LoadingSpinner /></div>
              ) : referralUsers.length === 0 ? (
                <div className="text-center py-14 px-6">
                  <FiUsers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No referral users match your search.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left font-medium text-slate-700">User</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Referral Code</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Referrals</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Earnings</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Rate</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Joined</th>
                        <th className="px-4 py-3 w-0" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {referralUsers.map((user) => (
                        <tr key={user._id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-slate-900">{user.firstName} {user.lastName}</div>
                              <div className="text-xs text-slate-500">{user.email}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{user.referralCode}</code>
                              <button type="button" onClick={() => copyToClipboard(user.referralCode, 'Code copied')} className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700" title="Copy"><FiCopy className="h-3.5 w-3.5" /></button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">{user.referralEarnings?.totalReferrals || 0}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{formatCurrency(user.referralEarnings?.totalAmount || 0)}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">{user.referralEarnings?.conversionRate || 0}%</span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => window.open(`/admin/users/${user._id}`, '_blank')} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700" title="View user"><FiEye className="h-4 w-4" /></button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRewardForm((f) => ({ ...f, userId: user._id, phoneNumber: user.phoneNumber || '' }));
                                  setShowRewardModal(true);
                                }}
                                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                title="Send reward"
                              >
                                <FiSend className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between px-2">
                <span className="text-xs text-slate-500">Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Previous</button>
                  <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Next</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Reward History */}
        {activeTab === 'rewards' && (
          <>
            <div className="flex flex-wrap items-center gap-6 py-3 px-4 rounded-lg bg-white border border-slate-200/80 shadow-sm mb-6">
              <div className="flex items-center gap-2">
                <FiGift className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-slate-600">Total Sent</span>
                <span className="text-sm font-semibold text-slate-900">{formatNumber(stats?.totalRewardsSent)}</span>
              </div>
              <div className="h-4 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Total Value</span>
                <span className="text-sm font-semibold text-slate-900">{formatCurrency(stats?.totalRewardValue)}</span>
              </div>
              <div className="h-4 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Success Rate</span>
                <span className="text-sm font-semibold text-slate-900">{((stats?.successfulRewards / stats?.totalRewardAttempts) * 100 || 0).toFixed(1)}%</span>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                <h3 className="text-sm font-medium text-slate-900">Reward Transaction History</h3>
              </div>
              <div className="p-4">
                {stats?.recentRewards?.length ? (
                  <ul className="divide-y divide-slate-100">
                    {stats.recentRewards.map((reward, index) => (
                      <li key={index} className="py-3 flex items-center justify-between gap-4">
                        <div>
                          <div className="font-medium text-slate-900">{reward.userName}</div>
                          <div className="text-xs text-slate-500">{reward.type} · {reward.phoneNumber} · {formatCurrency(reward.amount)}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(reward.status)}`}>{reward.status}</span>
                          <div className="text-xs text-slate-500 mt-0.5">{reward.date}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500 py-4">No reward history yet.</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* QR Generator */}
        {activeTab === 'qr' && (
          <>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Referral Code</label>
                  <input
                    type="text"
                    value={qrParams.code}
                    onChange={(e) => setQrParams((p) => ({ ...p, code: e.target.value }))}
                    placeholder="e.g. ABC123"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Select User (optional)</label>
                  <select
                    value={qrParams.userId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selected = qrUsers.find((u) => u._id === selectedId);
                      setQrParams((p) => ({
                        ...p,
                        userId: selectedId,
                        code: (!p.code && selected?.referralCode) ? selected.referralCode : p.code
                      }));
                    }}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  >
                    <option value="">— Select a user —</option>
                    {qrUsers.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName} · {user.email} {user.referralCode ? `· ${user.referralCode}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Search users</label>
                  <input
                    type="text"
                    value={qrSearch}
                    onChange={(e) => setQrSearch(e.target.value)}
                    placeholder="Name or email"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  />
                </div>
                <div className="md:col-span-3">
                  <button type="button" onClick={fetchAdminQRCode} disabled={qrGenerating} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50">
                    {qrGenerating ? 'Generating…' : 'Generate QR'}
                  </button>
                </div>
              </div>
            </div>

            {qrData && (
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden mb-6">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                  <h3 className="text-sm font-medium text-slate-900">Referral URL</h3>
                </div>
                <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <span className="text-sm text-slate-700 break-all flex-1 min-w-0">{qrData.referralUrl}</span>
                  <button type="button" onClick={() => copyToClipboard(qrData.referralUrl, 'Copied referral URL')} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 shrink-0">
                    <FiCopy className="h-4 w-4" /> Copy URL
                  </button>
                </div>
                <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/80">
                  <h3 className="text-sm font-medium text-slate-900">QR Code</h3>
                </div>
                <div className="p-4">
                  <img src={qrData.qrCode} alt="Referral QR Code" className="w-48 h-48 object-contain" />
                </div>
              </div>
            )}

            {qrSaved?.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                  <h3 className="text-sm font-medium text-slate-900">Previously Generated</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {qrSaved.map((item, idx) => (
                    <div key={idx} className="rounded-lg border border-slate-200 p-3 flex flex-col sm:flex-row sm:items-start gap-3">
                      <div className="min-w-0 flex-1 text-sm">
                        <div><span className="text-slate-500">Code:</span> <code className="bg-slate-100 px-1 rounded">{item.referralCode || '—'}</code></div>
                        <div className="text-slate-600 break-all mt-0.5"><span className="text-slate-500">URL:</span> {item.referralUrl}</div>
                        {item.assigned && <div className="text-emerald-600 text-xs mt-1">Assigned to user</div>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button type="button" onClick={() => setQrData(item)} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50">View</button>
                        <img src={item.qrCode} alt="Saved QR" className="w-36 h-36 object-contain" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Send Reward Modal */}
      {showRewardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowRewardModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Send Reward</h3>
            <form onSubmit={handleSendReward} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reward Type</label>
                <select
                  value={rewardForm.type}
                  onChange={(e) => setRewardForm((f) => ({ ...f, type: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  required
                >
                  <option value="registration_bonus">Registration Bonus (₦200)</option>
                  <option value="referral_bonus">Referral Bonus (₦100)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={rewardForm.phoneNumber}
                  onChange={(e) => setRewardForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  placeholder="08012345678"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowRewardModal(false)} className="px-4 py-2.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800">
                  <FiSend className="h-4 w-4" /> Send Reward
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralManagement;
